import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchUserData, logout } from "../redux/authSlice";
import { useNavigate } from "react-router-dom";
import logo from "../assets/cosmeta/logo.png";
import { Modal, Fade, Typography } from "@mui/material";
import { CheckCircle, ContentCopy, Share } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell, Camera, Shield, ShieldCheck, Package, RefreshCw,
  RotateCcw, CreditCard, Headphones, Pencil, Store,
  Home, BarChart2, Settings, LogOut, Plus, Copy, Users,
  ChevronRight, Wallet, LayoutDashboard, ClipboardList,
  UserCheck, MapPin, Star, TrendingUp,
} from "lucide-react";

/* ─── Palette — Luxury B&W ──────────────────────────────── */
const WHITE   = "#ffffff";
const OFF     = "#e8e8e8";
const BG      = "#000000";
const SURFACE = "#0a0a0a";
const SURF2   = "#101010";
const DARK    = "#000000";
const MUTED   = "#6b6b6b";
const BORDER  = "#1c1c1c";
const BORDER2 = "#2a2a2a";
const SUBTLE  = "#3a3a3a";

/* ─── Font injection ────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

const maskName = (name: string) => {
  if (!name) return "";
  if (name.length <= 2) return name;
  return name.slice(0, 2) + "·".repeat(name.length - 2);
};
const maskEmail = (email: string) => {
  if (!email) return "";
  const [u, d] = email.split("@");
  if (!d) return email;
  return `${u.slice(0, 3)}···@${d}`;
};

const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const userId = user?.userId;

  const [copied, setCopied]                 = useState(false);
  const [instantRefunds, setInstantRefunds] = useState(false);

  useEffect(() => { dispatch(fetchUserData()); }, [dispatch]);

  const profileCompletion = () => {
    const fields = ["firstName","lastName","emailAddress","phoneNumber","username","profilePicture","userAge","userAddress","verifyEmail"];
    const filled = fields.filter(f => {
      const v = f === "userAddress"
        ? user?.userAddress?.city && user.userAddress.streetAddress
        : user?.[f as keyof typeof user];
      return !!v;
    });
    return Math.round((filled.length / fields.length) * 100);
  };

  const isVerified = user?.isVerified === true;
  const completion = profileCompletion();
  const firstName  = maskName(user?.firstName || "First");
  const lastName   = maskName(user?.lastName  || "Last");
  const email      = maskEmail(user?.emailAddress || user?.email || "email@example.com");
  const photo      = user?.profilePicture || user?.photoURL || null;
  const initials   = `${user?.firstName?.[0]||""}${user?.lastName?.[0]||""}`.toUpperCase() || "U";

  /* ── Logged-out view ───────────────────────────────────── */
  if (!user) {
    return (
      <div style={M.root}>
        <style>{FONT_CSS + RESP_CSS}</style>
        <div className="mobile-only" style={M.mobileWrapper}>
          <div style={M.header}>
            <div style={M.topBar}>
              <span style={M.pageTitle}>Profile</span>
            </div>
          </div>
          <div style={{ padding:"40px 24px", textAlign:"center" }}>
            <h2 style={{ color:WHITE, fontSize:26, fontWeight:300, marginBottom:10, fontFamily:"'Cormorant Garamond', serif", letterSpacing:"0.06em" }}>Your Profile</h2>
            <p style={{ color:MUTED, marginBottom:28, fontSize:13, letterSpacing:"0.03em" }}>Login to access your COSMETA account</p>
            <button onClick={() => navigate("/AuthSwitch")} style={M.primaryBtn}>LOGIN</button>
            <p style={{ color:MUTED, marginTop:18, fontSize:12 }}>
              Don't have an account?{" "}
              <span style={{ color:WHITE, cursor:"pointer", fontWeight:500, borderBottom:`1px solid ${BORDER2}`, paddingBottom:1 }} onClick={() => navigate("/AuthSwitch")}>Register</span>
            </p>
            <div style={{ margin:"32px 0 24px", height:1, backgroundColor:BORDER }} />
            {PUBLIC_LINKS.map(item => (
              <div key={item.label} onClick={item.fn} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${BORDER}`, padding:"16px 0", cursor:"pointer" }}>
                <span style={{ color:OFF, fontWeight:300, fontSize:16, fontFamily:"'Cormorant Garamond', serif", letterSpacing:"0.02em" }}>{item.label}</span>
                <ChevronRight size={18} color={SUBTLE} />
              </div>
            ))}
          </div>
        </div>
        {/* Desktop logged-out */}
        <div className="desktop-only" style={D.root}>
          <DeskNav logo={logo} userId={userId} user={user} navigate={navigate} handleDashboard={() => navigate("/AuthSwitch")}/>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"80vh", gap:24 }}>
            <h2 style={{ color:WHITE, fontSize:36, fontWeight:300, fontFamily:"'Cormorant Garamond', serif", letterSpacing:"0.06em" }}>Sign in to COSMETA</h2>
            <p style={{ color:MUTED, fontSize:14, letterSpacing:"0.05em" }}>Login to access your profile</p>
            <button onClick={() => navigate("/AuthSwitch")} style={{ background:"transparent", border:`1px solid ${WHITE}`, color:WHITE, fontWeight:500, borderRadius:0, padding:"14px 52px", cursor:"pointer", fontSize:12, letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" }}>LOGIN</button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Referral section ──────────────────────────────────── */
  const ReferralBlock = ({ dark }: { dark?: boolean }) => !user?.username ? null : (
    <div style={dark ? D.referralCard : M.referralCard}>
      <div style={{ fontSize:10, color:MUTED, letterSpacing:"0.2em", marginBottom:10 }}>REFER & EARN</div>
      <div style={{ fontWeight:400, fontSize:18, color:WHITE, marginBottom:6, fontFamily:"'Cormorant Garamond', serif" }}>Invite friends, earn €10 each</div>
      <p style={{ color:MUTED, fontSize:12, marginBottom:14, lineHeight:"1.6" }}>
        Share your link — you both earn €10 on their first booking.
      </p>
      <div style={{ display:"flex", gap:8, marginBottom:10 }}>
        <input readOnly value={`COSMETA.store/ref/${user.username}`}
          style={{ flex:1, borderRadius:0, padding:"10px 12px", border:`1px solid ${BORDER}`, fontSize:11, color:MUTED, outline:"none", background:"rgba(255,255,255,0.03)", fontFamily:"monospace" }}/>
        <button style={{ background:WHITE, color:DARK, fontWeight:600, border:`1px solid ${WHITE}`, borderRadius:0, padding:"10px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:11, letterSpacing:"0.1em", fontFamily:"'Montserrat', sans-serif" }}
          onClick={async () => { await navigator.clipboard.writeText(`https://COSMETA.store/ref/${user.username}`); setCopied(true); setTimeout(()=>setCopied(false),2000); }}>
          <Copy size={13}/> COPY
        </button>
      </div>
      <button style={{ width:"100%", background:"transparent", color:WHITE, fontWeight:500, border:`1px solid ${BORDER}`, borderRadius:0, padding:"12px 0", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontSize:12, letterSpacing:"0.1em", fontFamily:"'Montserrat', sans-serif" }}
        onClick={async () => { if(navigator.share) await navigator.share({ title:"Join COSMETA", text:"Earn rewards!", url:`https://COSMETA.store/ref/${user.username}` }); else alert("Sharing not supported."); }}>
        <Users size={15}/> INVITE FRIENDS
      </button>
      <Modal open={copied} onClose={()=>setCopied(false)}>
        <Fade in={copied}>
          <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", background:DARK, borderRadius:0, padding:"50px 40px", outline:"none", textAlign:"center", border:`1px solid ${WHITE}` }}>
            <AnimatePresence>
              {copied && (
                <motion.div initial={{scale:0,opacity:0}} animate={{scale:1.2,opacity:1}} exit={{scale:0,opacity:0}} transition={{duration:0.4}}>
                  <CheckCircle style={{fontSize:70,color:WHITE}}/>
                  <Typography variant="h6" sx={{color:WHITE,mt:1,fontFamily:"'Cormorant Garamond', serif",letterSpacing:"0.1em"}}>Link Copied</Typography>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Fade>
      </Modal>
    </div>
  );

  return (
    <div style={M.root}>
      <style>{FONT_CSS + RESP_CSS}</style>

      {/* ══════════ MOBILE ══════════ */}
      <div className="mobile-only" style={M.mobileWrapper}>
        {/* Header */}
        <div style={M.header}>
          <div style={M.topBar}>
            <span style={M.pageTitle}>Profile</span>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              {window.location.hostname==="localhost"
                ? <button onClick={()=>{if(user?.isAdmin||user?.isDirector) window.open(`/OperationsManagement/${userId}`,"_blank");}} style={M.logoBtn}><img src={logo} alt="ops" style={{width:"100%",height:"100%",objectFit:"cover"}}/></button>
                : <button style={{...M.logoBtn,opacity:0.3,cursor:"not-allowed"}} disabled><img src={logo} alt="ops" style={{width:"100%",height:"100%",objectFit:"cover"}}/></button>
              }
              <button style={M.bellBtn} onClick={()=>navigate(`/Notifications/${userId}`)}><Bell size={18} color={WHITE}/></button>
            </div>
          </div>
          <div style={M.avatarSection}>
            <div style={M.avatarOuter}>
              <div style={{position:"relative"}}>
                {photo ? <img src={photo} alt="avatar" style={M.avatar}/> : <div style={M.avatarPlaceholder}><span style={M.avatarInitials}>{initials}</span></div>}
                <button style={M.cameraIcon} onClick={()=>navigate(`/EditProfile/${userId}`)}><Camera size={11} color={WHITE}/></button>
                {isVerified && <div style={M.verifiedBadge}><ShieldCheck size={12} color={WHITE}/></div>}
              </div>
            </div>
            <div style={{flex:1}}>
              <div style={M.userName}>{firstName} {lastName}</div>
              <div style={M.userEmail}>{email}</div>
              <div style={isVerified ? M.pillVerified : M.pillPending}>
                {isVerified ? <ShieldCheck size={11} color={WHITE}/> : <Shield size={11} color={MUTED}/>}
                <span style={{fontSize:9,fontWeight:500,color:isVerified?WHITE:MUTED,letterSpacing:"0.12em"}}>{isVerified?"VERIFIED":"UNVERIFIED"}</span>
              </div>
              <div style={{color:OFF,fontSize:11,fontWeight:400,marginTop:4,letterSpacing:"0.05em"}}>{completion}% Complete</div>
            </div>
          </div>
          <div style={M.refundCard}>
            <div style={{flex:1,marginRight:12}}>
              <div style={M.refundTitle}>Instant Refunds</div>
              <div style={M.refundSub}>Direct refunds to your wallet</div>
            </div>
            <label style={{cursor:"pointer"}}>
              <input type="checkbox" checked={instantRefunds} onChange={()=>setInstantRefunds(v=>!v)} style={{display:"none"}}/>
              <div style={{width:46,height:26,borderRadius:0,background:instantRefunds?WHITE:"rgba(255,255,255,0.15)",position:"relative",transition:"background 0.2s",display:"flex",alignItems:"center",border:`1px solid ${instantRefunds?WHITE:BORDER2}`}}>
                <div style={{width:20,height:20,borderRadius:0,backgroundColor:instantRefunds?DARK:MUTED,position:"absolute",transition:"transform 0.2s",transform:instantRefunds?"translateX(22px)":"translateX(2px)"}}/>
              </div>
            </label>
          </div>
        </div>

        {/* Body */}
        <div style={M.body}>
          <div style={M.bannerRow}>
            <button style={M.bannerCard} onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}>
              <div style={M.bannerIcon}><TrendingUp size={18} color={WHITE}/></div>
              <div style={{textAlign:"left"}}><div style={M.bannerSub}>No SHARE points?</div><div style={M.bannerAction}>Earn Today →</div></div>
            </button>
            <button style={M.bannerCard} onClick={()=>navigate("/Reorder")}>
              <div style={M.bannerIcon}><Star size={18} color={WHITE}/></div>
              <div style={{textAlign:"left"}}><div style={M.bannerSub}>No savings yet?</div><div style={M.bannerAction}>Start Shopping!</div></div>
            </button>
          </div>

          <div style={M.actionRow}>
            <MobActionCard icon={<Package size={22} color={WHITE}/>}     label="My Orders"  highlight onClick={()=>navigate(`/MetaClientDashboard/${userId}`)}/>
                        <MobActionCard icon={<Package size={22} color={WHITE}/>}     label="My Dashboard"  highlight onClick={()=>navigate(`/MetaCommerceDashboard/${userId}`)}/>

            <MobActionCard icon={<RefreshCw size={22} color={MUTED}/>}   label="Returns"             onClick={()=>navigate(`/Returns/${userId}`)}/>
            <MobActionCard icon={<RotateCcw size={22} color={MUTED}/>}   label="Buy Again"           onClick={()=>navigate(`/Reorder/${userId}`)}/>
          </div>

          <div style={M.sectionTitle}>MY ADDRESSES</div>
          <div style={M.addressCard}>
            <p style={{color:MUTED,fontSize:13,marginBottom:14}}>Haven't added a delivery address yet?</p>
            <button style={M.addAddressBtn} onClick={()=>navigate(`/EditProfile/${userId}`)}>
              <Plus size={14} color={WHITE} style={{marginRight:6}}/> Add a New Address
            </button>
          </div>

          <MobListItem icon={<CreditCard size={20} color={OFF}/>}   label="Wallet & Saved Cards"         onClick={()=>navigate("/PaymentMethodDashboard")}/>
          <MobListItem icon={<Headphones size={20} color={OFF}/>}   label="Customer Service"              onClick={()=>navigate("/CustomerSupport")} right={<div style={M.supportBtn}><Headphones size={16} color={DARK}/></div>}/>
          <MobListItem icon={<Pencil size={20} color={OFF}/>}       label="Edit Profile"                  onClick={()=>navigate(`/EditProfile/${userId}`)}/>
          <MobListItem icon={<ShieldCheck size={20} color={OFF}/>}  label="Verify Profile"                onClick={()=>navigate("/VerifyProfile")}/>
          <MobListItem icon={<Store size={20} color={OFF}/>}        label="Sell on COSMETA"              onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>
          <MobListItem icon={<Settings size={20} color={OFF}/>}     label="Settings & Privacy"            onClick={()=>navigate(`/ProfileSettings/${userId}`)}/>
          <MobListItem icon={<Bell size={20} color={OFF}/>}         label="Notifications"                 onClick={()=>navigate(`/Notifications/${userId}`)}/>

          <ReferralBlock/>

          <button style={M.logoutBtn} onClick={async () => { await dispatch(logout()).unwrap(); navigate("/AuthSwitch"); }}>
            <LogOut size={18} color={DARK} style={{marginRight:10}}/> LOG OUT
          </button>
        </div>
      </div>

      {/* ══════════ DESKTOP ══════════ */}
      <div className="desktop-only" style={D.root}>
        <DeskNav logo={logo} userId={userId} user={user} navigate={navigate} handleDashboard={()=>navigate(`/GlamourManagementDashboard/${userId}`)}/>

        {/* Hero banner */}
        <div style={D.heroBanner}>
          <div style={D.heroLeft}>
            <div style={D.heroAvatarOuter}>
              <div style={{position:"relative"}}>
                {photo ? <img src={photo} alt="avatar" style={D.heroAvatar}/> : <div style={D.heroAvatarPlaceholder}><span style={D.heroInitials}>{initials}</span></div>}
                <button style={D.heroCamBtn} onClick={()=>navigate(`/EditProfile/${userId}`)}><Camera size={14} color={WHITE}/></button>
                {isVerified && <div style={D.heroVerBadge}><ShieldCheck size={14} color={WHITE}/></div>}
              </div>
            </div>
            <div>
              <div style={D.heroName}>{firstName} {lastName}</div>
              <div style={D.heroEmail}>{email}</div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginTop:12}}>
                <div style={isVerified ? M.pillVerified : M.pillPending}>
                  {isVerified ? <ShieldCheck size={11} color={WHITE}/> : <Shield size={11} color={MUTED}/>}
                  <span style={{fontSize:9,fontWeight:500,color:isVerified?WHITE:MUTED,letterSpacing:"0.12em"}}>{isVerified?"VERIFIED":"UNVERIFIED"}</span>
                </div>
                <span style={{color:OFF,fontSize:11,fontWeight:400,letterSpacing:"0.05em"}}>{completion}% Complete</span>
              </div>
            </div>
          </div>

          <div style={D.heroRight}>
            <div style={D.heroToggleCard}>
              <div>
                <div style={{color:WHITE,fontWeight:500,fontSize:13,marginBottom:3,letterSpacing:"0.03em"}}>Instant Refunds</div>
                <div style={{color:MUTED,fontSize:11}}>Direct refunds to your wallet</div>
              </div>
              <label style={{cursor:"pointer"}}>
                <input type="checkbox" checked={instantRefunds} onChange={()=>setInstantRefunds(v=>!v)} style={{display:"none"}}/>
                <div style={{width:52,height:28,borderRadius:0,background:instantRefunds?WHITE:"rgba(255,255,255,0.1)",position:"relative",transition:"background 0.2s",display:"flex",alignItems:"center",border:`1px solid ${instantRefunds?WHITE:BORDER2}`}}>
                  <div style={{width:22,height:22,borderRadius:0,backgroundColor:instantRefunds?DARK:MUTED,position:"absolute",transition:"transform 0.2s",transform:instantRefunds?"translateX(26px)":"translateX(3px)"}}/>
                </div>
              </label>
            </div>
            <div style={D.heroStats}>
              <HeroStat icon={<Package size={18} color={WHITE}/>}     label="Orders"  onClick={()=>navigate(`/MetaClientDashboard/${userId}`)}/>
              <HeroStat icon={<Wallet size={18} color={OFF}/>}        label="Wallet"  onClick={()=>navigate(`/PaymentMethodDashboard/${userId}`)}/>
              <HeroStat icon={<Star size={18} color={WHITE}/>}        label="Points"  onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>
              <HeroStat icon={<TrendingUp size={18} color={OFF}/>}    label="Savings" onClick={()=>navigate(`/Reorder/${userId}`)}/>
            </div>
          </div>
        </div>

        {/* 3-column grid */}
        <div style={D.grid}>

          {/* COL 1 — Account */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>ACCOUNT</span></div>
            <DeskListItem icon={<Pencil size={15}/>}     label="Edit Profile"       sub="Update your info"       onClick={()=>navigate(`/EditProfile/${userId}`)}/>
            <DeskListItem icon={<UserCheck size={15}/>}  label="Verify Profile"     sub="Complete verification"  onClick={()=>navigate("/VerifyProfile")}/>
            <DeskListItem icon={<MapPin size={15}/>}     label="My Addresses"       sub="Manage delivery spots"  onClick={()=>navigate(`/EditProfile/${userId}`)} badge="Add"/>
            <DeskListItem icon={<Settings size={15}/>}   label="Settings & Privacy" sub="Security & preferences" onClick={()=>navigate(`/ProfileSettings/${userId}`)}/>
            <DeskListItem icon={<Bell size={15}/>}       label="Notifications"      sub="Alerts & updates"       onClick={()=>navigate(`/Notifications/${userId}`)}/>
            <button onClick={async()=>{ await dispatch(logout()).unwrap(); navigate("/AuthSwitch"); }} style={D.logoutBtn}><LogOut size={15}/><span>LOG OUT</span></button>
          </div>

          {/* COL 2 — Commerce */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>COMMERCE</span></div>
            <DeskListItem icon={<Store size={15}/>}      label="Sell on COSMETA"   sub="List your products"     onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>
            <DeskListItem icon={<Package size={15}/>}    label="My Orders"          sub="Track & manage orders"  onClick={()=>navigate(`/MetaClientDashboard/${userId}`)} badge="New"/>
            <DeskListItem icon={<RefreshCw size={15}/>}  label="Returns"            sub="Start a return"         onClick={()=>navigate(`/Returns/${userId}`)}/>
            <DeskListItem icon={<RotateCcw size={15}/>}  label="Buy Again"          sub="Reorder favourites"     onClick={()=>navigate(`/Reorder/${userId}`)}/>
            <DeskListItem icon={<CreditCard size={15}/>} label="Wallet & Cards"     sub="Payments & balance"     onClick={()=>navigate(`/PaymentMethodDashboard/${userId}`)}/>
            <DeskListItem icon={<Headphones size={15}/>} label="Customer Service"   sub="Get help instantly"     onClick={()=>navigate("/CustomerSupport/")} badge="Chat"/>
          </div>

          {/* COL 3 — Hosting + Referral */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>DASHBOARD</span></div>
            <DeskListItem icon={<BarChart2 size={15}/>}     label="Commerce Dashboard" sub="Manage your business" onClick={()=>navigate(`/GlamourDashboard/${userId}`)}/>
            <div style={{marginTop:24}}>
              <div style={D.colHeader}><span style={D.colTitle}>REFERRALS</span></div>
              <ReferralBlock dark/>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;

/* ── Desktop Nav ───────────────────────────────────────────── */
const DeskNav = ({ logo, userId, user, navigate, handleDashboard }: any) => (
  <nav style={D.nav}>
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <img src={logo} alt="COSMETA" style={{ height:28, width:28, borderRadius:0 }}/>
      <span style={D.navBrandText}>COSMETA</span>
    </div>
    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
      <NavLink icon={<LayoutDashboard size={15}/>} label="Dashboard"  onClick={handleDashboard}/>
      <NavLink icon={<Package size={15}/>}         label="My Orders"  onClick={()=>navigate(`/MetaClientDashboard/${userId}`)}/>
      <NavLink icon={<Store size={15}/>}           label="Sell"       onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}/>
            <NavLink icon={<Store size={15}/>}           label="My Dashboard"       onClick={()=>navigate(`/MetaCommerceDashboard/${userId}`)}/>

    </div>
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <button style={D.navIconBtn} onClick={()=>navigate(`/Notifications/${userId}`)}><Bell size={18} color={MUTED}/></button>
      <button style={D.navIconBtn} onClick={()=>navigate(`/ProfileSettings/${userId}`)}><Settings size={18} color={MUTED}/></button>
      {window.location.hostname==="localhost"
        ? <button onClick={()=>{if(user?.isAdmin||user?.isDirector) window.open(`/OperationsManagement/${userId}`,"_blank");}} style={D.logoBtn}><img src={logo} alt="ops" style={{width:"100%",height:"100%",objectFit:"cover"}}/></button>
        : <button style={{...D.logoBtn,opacity:0.3,cursor:"not-allowed"}} disabled><img src={logo} alt="ops" style={{width:"100%",height:"100%",objectFit:"cover"}}/></button>
      }
    </div>
  </nav>
);

/* ── Sub-components ─────────────────────────────────────────── */
const NavLink = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button onClick={onClick}
    style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:MUTED, fontSize:12, fontWeight:500, padding:"8px 16px", borderRadius:0, fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.05em", transition:"color 0.2s" }}
    onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.color=WHITE;}}
    onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.color=MUTED;}}>
    {icon}{label}
  </button>
);

const HeroStat = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button onClick={onClick}
    style={{ background:"rgba(255,255,255,0.03)", border:`1px solid ${BORDER}`, borderRadius:0, padding:"16px 20px", cursor:"pointer", display:"flex", flexDirection:"column", gap:8, alignItems:"center", flex:1, transition:"border-color 0.2s" }}
    onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=WHITE;}}
    onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=BORDER;}}>
    {icon}
    <span style={{ color:MUTED, fontSize:10, fontWeight:500, letterSpacing:"0.12em", fontFamily:"'Montserrat', sans-serif" }}>{label.toUpperCase()}</span>
  </button>
);

const DeskListItem = ({ icon, label, sub, onClick, badge }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void; badge?: string }) => (
  <button onClick={onClick} style={D.listItem}
    onMouseEnter={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.borderColor=WHITE;}}
    onMouseLeave={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.borderColor=BORDER;}}>
    <div style={D.listIconWrap}>{icon}</div>
    <div style={{ flex:1, textAlign:"left" }}>
      <div style={{ fontSize:13, fontWeight:500, color:OFF }}>{label}</div>
      <div style={{ fontSize:11, color:MUTED, marginTop:2 }}>{sub}</div>
    </div>
    {badge
      ? <span style={{ background:WHITE, color:DARK, fontSize:9, fontWeight:600, borderRadius:0, padding:"3px 10px", letterSpacing:"0.1em" }}>{badge.toUpperCase()}</span>
      : <ChevronRight size={15} color={SUBTLE}/>
    }
  </button>
);

const MobActionCard = ({ icon, label, onClick, highlight }: { icon: React.ReactNode; label: string; onClick: () => void; highlight?: boolean }) => (
  <button onClick={onClick} style={{ flex:1, background:SURF2, borderRadius:0, paddingTop:22, paddingBottom:22, display:"flex", flexDirection:"column", alignItems:"center", gap:8, border:`1px solid ${highlight?WHITE:BORDER}`, cursor:"pointer" }}>
    {icon}
    <span style={{ fontSize:10, fontWeight:500, color:highlight?WHITE:MUTED, textAlign:"center", letterSpacing:"0.08em", fontFamily:"'Montserrat', sans-serif" }}>{label.toUpperCase()}</span>
  </button>
);

const MobListItem = ({ icon, label, onClick, right }: { icon: React.ReactNode; label: string; onClick: () => void; right?: React.ReactNode }) => (
  <button onClick={onClick} style={M.listItem}>
    <div style={M.listIconWrap}>{icon}</div>
    <span style={M.listLabel}>{label}</span>
    {right ?? <ChevronRight size={18} color={SUBTLE} style={{ marginLeft:"auto" }}/>}
  </button>
);

/* ── Public links config ────────────────────────────────────── */
const PUBLIC_LINKS = [
  { label:"Settings",            fn: () => window.location.href="/GlobalSettings" },
  { label:"Accessibility",       fn: () => window.open("/GlobalSettings","_blank") },
  { label:"Learn About Hosting", fn: () => window.open("/LearnHosting","_blank") },
  { label:"Get help",            fn: () => window.open("/Help","_blank") },
  { label:"Terms of Service",    fn: () => window.open("/terms","_blank") },
  { label:"Privacy Policy",      fn: () => window.open("/Privacy","_blank") },
];

/* ── CSS strings ────────────────────────────────────────────── */
const RESP_CSS = `
  .desktop-only{display:none}
  .mobile-only{display:block}
  @media(min-width:900px){
    .desktop-only{display:block!important}
    .mobile-only{display:none!important}
  }
`;

/* ── Mobile styles ──────────────────────────────────────────── */
const M: { [k: string]: React.CSSProperties } = {
  root:          { minHeight:"100vh", backgroundColor:BG, display:"flex", justifyContent:"center", fontFamily:"'Montserrat', 'Cormorant Garamond', sans-serif" },
  mobileWrapper: { width:"100%", maxWidth:500 },
  header:        { position:"relative", overflow:"hidden", background:BG, padding:"0 20px 26px", borderBottom:`1px solid ${BORDER}` },
  topBar:        { display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"space-between", paddingTop:54, marginBottom:24 },
  pageTitle:     { color:WHITE, fontSize:24, fontWeight:300, fontFamily:"'Cormorant Garamond', serif", letterSpacing:"0.12em" },
  logoBtn:       { width:36, height:36, borderRadius:0, background:"rgba(255,255,255,0.06)", border:`1px solid ${BORDER}`, cursor:"pointer", overflow:"hidden", padding:0 },
  bellBtn:       { width:36, height:36, borderRadius:0, backgroundColor:"rgba(255,255,255,0.06)", border:`1px solid ${BORDER}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  avatarSection: { display:"flex", alignItems:"center", gap:18, marginBottom:22 },
  avatarOuter:   { position:"relative", width:84, height:84, display:"flex", alignItems:"center", justifyContent:"center" },
  avatar:            { width:76, height:76, borderRadius:"50%", border:`1.5px solid ${BORDER2}`, objectFit:"cover", display:"block" },
  avatarPlaceholder: { width:76, height:76, borderRadius:"50%", backgroundColor:SURF2, border:`1.5px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center" },
  avatarInitials:    { color:WHITE, fontSize:22, fontWeight:300, fontFamily:"'Cormorant Garamond', serif" },
  cameraIcon:    { position:"absolute", bottom:2, right:2, backgroundColor:BG, borderRadius:0, width:24, height:24, border:`1px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" },
  verifiedBadge: { position:"absolute", top:-2, left:-2, width:22, height:22, borderRadius:0, backgroundColor:SURF2, border:`1px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center" },
  userName:      { color:WHITE, fontSize:18, fontWeight:400, marginBottom:2, fontFamily:"'Cormorant Garamond', serif", letterSpacing:"0.04em" },
  userEmail:     { color:MUTED, fontSize:11, fontWeight:400, marginBottom:8, letterSpacing:"0.03em" },
  pillVerified:  { display:"inline-flex", alignItems:"center", gap:5, borderRadius:0, padding:"3px 10px", backgroundColor:"rgba(255,255,255,0.06)", border:`1px solid ${WHITE}`, marginBottom:4 },
  pillPending:   { display:"inline-flex", alignItems:"center", gap:5, borderRadius:0, padding:"3px 10px", backgroundColor:"rgba(255,255,255,0.03)", border:`1px solid ${BORDER}`, marginBottom:4 },
  refundCard:    { backgroundColor:"rgba(255,255,255,0.03)", border:`1px solid ${BORDER}`, borderRadius:0, padding:16, display:"flex", alignItems:"center", justifyContent:"space-between" },
  refundTitle:   { color:WHITE, fontWeight:500, fontSize:13, marginBottom:3, letterSpacing:"0.03em" },
  refundSub:     { color:MUTED, fontSize:11, lineHeight:"16px" },
  body:          { padding:"24px 16px 40px" },
  bannerRow:     { display:"flex", gap:12, marginBottom:16 },
  bannerCard:    { flex:1, backgroundColor:SURF2, borderRadius:0, padding:14, display:"flex", alignItems:"center", gap:12, border:`1px solid ${BORDER}`, cursor:"pointer", textAlign:"left" },
  bannerIcon:    { width:38, height:38, backgroundColor:"rgba(255,255,255,0.04)", borderRadius:0, display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${BORDER}` },
  bannerSub:     { fontSize:10, color:MUTED, marginBottom:2, letterSpacing:"0.03em" },
  bannerAction:  { fontSize:12, fontWeight:500, color:WHITE, letterSpacing:"0.03em" },
  actionRow:     { display:"flex", gap:10, marginBottom:22 },
  sectionTitle:  { fontSize:10, fontWeight:500, color:MUTED, marginBottom:12, letterSpacing:"0.2em" },
  addressCard:   { backgroundColor:SURF2, borderRadius:0, padding:22, textAlign:"center", marginBottom:14, border:`1px solid ${BORDER}` },
  addAddressBtn: { border:`1px solid ${BORDER}`, borderRadius:0, padding:"10px 24px", background:"transparent", cursor:"pointer", color:WHITE, fontWeight:500, fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", letterSpacing:"0.08em", fontFamily:"'Montserrat', sans-serif" },
  listItem:      { backgroundColor:SURF2, borderRadius:0, padding:"14px 16px", display:"flex", alignItems:"center", marginBottom:8, width:"100%", border:`1px solid ${BORDER}`, cursor:"pointer", textAlign:"left", fontFamily:"inherit" },
  listIconWrap:  { width:38, height:38, borderRadius:0, backgroundColor:"rgba(255,255,255,0.03)", border:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"center", marginRight:14, flexShrink:0 },
  listLabel:     { fontSize:13, fontWeight:500, color:OFF },
  supportBtn:    { backgroundColor:WHITE, borderRadius:0, width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", marginLeft:"auto" },
  logoutBtn:     { width:"100%", borderRadius:0, padding:"16px 16px", background:WHITE, border:"none", cursor:"pointer", color:DARK, fontWeight:500, fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:8, letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },
  referralCard:  { backgroundColor:SURF2, borderRadius:0, padding:20, border:`1px solid ${BORDER}`, marginTop:12, marginBottom:18 },
  primaryBtn:    { background:"transparent", border:`1px solid ${WHITE}`, color:WHITE, fontWeight:500, borderRadius:0, padding:"14px 40px", cursor:"pointer", fontSize:12, letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },
};

/* ── Desktop styles ─────────────────────────────────────────── */
const D: { [k: string]: React.CSSProperties } = {
  root:          { minHeight:"100vh", backgroundColor:BG, fontFamily:"'Montserrat', 'Cormorant Garamond', sans-serif" },
  nav:           { position:"sticky", top:0, zIndex:100, background:BG, borderBottom:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 40px", height:64 },
  navBrandText:  { color:WHITE, fontSize:15, fontWeight:400, letterSpacing:"0.3em", fontFamily:"'Cormorant Garamond', serif" },
  navIconBtn:    { width:38, height:38, borderRadius:0, background:"transparent", border:`1px solid ${BORDER}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  logoBtn:       { width:34, height:34, borderRadius:0, background:"rgba(255,255,255,0.06)", border:`1px solid ${BORDER}`, cursor:"pointer", overflow:"hidden", padding:0 },

  heroBanner:    { position:"relative", overflow:"hidden", background:BG, padding:"52px 56px 48px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:48, borderBottom:`1px solid ${BORDER}` },
  heroLeft:      { display:"flex", alignItems:"center", gap:28 },
  heroAvatarOuter: { position:"relative", width:110, height:110, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  heroAvatar:            { width:96, height:96, borderRadius:"50%", border:`1.5px solid ${BORDER2}`, objectFit:"cover", display:"block" },
  heroAvatarPlaceholder: { width:96, height:96, borderRadius:"50%", backgroundColor:SURF2, border:`1.5px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center" },
  heroInitials:  { color:WHITE, fontSize:30, fontWeight:300, fontFamily:"'Cormorant Garamond', serif" },
  heroCamBtn:    { position:"absolute", bottom:4, right:4, backgroundColor:BG, borderRadius:0, width:30, height:30, border:`1px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" },
  heroVerBadge:  { position:"absolute", top:-2, left:-2, width:26, height:26, borderRadius:0, backgroundColor:SURF2, border:`1px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center" },
  heroName:      { color:WHITE, fontSize:32, fontWeight:300, letterSpacing:"0.04em", fontFamily:"'Cormorant Garamond', serif" },
  heroEmail:     { color:MUTED, fontSize:12, marginTop:4, letterSpacing:"0.03em" },
  heroRight:     { display:"flex", flexDirection:"column", gap:16, flex:1, maxWidth:580 },
  heroToggleCard:{ backgroundColor:"rgba(255,255,255,0.03)", border:`1px solid ${BORDER}`, borderRadius:0, padding:"18px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" },
  heroStats:     { display:"flex", gap:12 },

  grid:          { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:32, padding:"44px 56px 64px", maxWidth:1400, margin:"0 auto" },
  col:           { display:"flex", flexDirection:"column" },
  colHeader:     { marginBottom:18, paddingBottom:14, borderBottom:`1px solid ${BORDER}` },
  colTitle:      { color:MUTED, fontSize:10, fontWeight:500, letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },

  listItem:      { backgroundColor:SURF2, border:`1px solid ${BORDER}`, borderRadius:0, padding:"14px 16px", display:"flex", alignItems:"center", marginBottom:8, width:"100%", cursor:"pointer", textAlign:"left", transition:"border-color 0.2s", fontFamily:"inherit" },
  listIconWrap:  { width:34, height:34, borderRadius:0, backgroundColor:"rgba(255,255,255,0.03)", border:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"center", marginRight:12, flexShrink:0, color:OFF },
  logoutBtn:     { marginTop:16, borderRadius:0, padding:"14px 16px", background:WHITE, border:"none", cursor:"pointer", color:DARK, fontWeight:500, fontSize:12, display:"flex", alignItems:"center", gap:10, justifyContent:"center", letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },
  referralCard:  { backgroundColor:SURF2, borderRadius:0, padding:20, border:`1px solid ${BORDER}`, marginTop:4 },
};